import { Select } from '@byecode/ui'
import type { DataSourceAbstract, FieldType } from '@lighthouse/core'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { NO_EDIT_SYSTEM_FIELD_OMIT_ID } from '../../constants'
import { FieldIcon } from '../FieldIcon'

interface SettingStepProps {
    dataSource: DataSourceAbstract
    noteFieldId: string
    matchFieldId: string
    onNoteFieldChange: (id: string) => void
    onMatchFieldChange: (id: string) => void
}

const matchableFieldTypes: Set<Partial<FieldType>> = new Set(['text', 'number', 'url', 'email', 'phoneNumber', 'singleSelect'])

const SettingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const FieldLabel = styled.div`
    font-size: var(--font-size-normal);
    color: var(--color-gray-600);
`

const FieldHint = styled.div`
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
`

export const SettingStep: React.FC<SettingStepProps> = ({ dataSource, noteFieldId, matchFieldId, onNoteFieldChange, onMatchFieldChange }) => {
    const { noteFieldOptions, matchFieldOptions } = useMemo(() => {
        const fields = Object.values(dataSource.schema ?? {})
        const noteFieldOptions = fields
            .filter(field => field.type === 'notes' && !NO_EDIT_SYSTEM_FIELD_OMIT_ID.has(field.id))
            .map(field => ({
                value: field.id,
                label: field.name,
                icon: <FieldIcon id={field.id} type={field.type} innerType={field.innerType} />
            }))
        const matchFieldOptions = fields
            .filter(field => matchableFieldTypes.has(field.type) && !NO_EDIT_SYSTEM_FIELD_OMIT_ID.has(field.id))
            .map(field => ({
                value: field.id,
                label: field.name,
                icon: <FieldIcon id={field.id} type={field.type} innerType={field.innerType} />
            }))
        return { noteFieldOptions, matchFieldOptions }
    }, [dataSource.schema])

    const noNoteField = noteFieldOptions.length === 0

    return (
        <SettingContainer>
            <FieldGroup>
                <FieldLabel>导入到的笔记字段</FieldLabel>
                <Select
                    options={noteFieldOptions}
                    value={noteFieldId}
                    onChange={onNoteFieldChange}
                    placeholder="请选择笔记字段"
                    searchable
                />
                {noNoteField && <FieldHint>当前数据表暂无笔记字段，请先创建</FieldHint>}
            </FieldGroup>
            <FieldGroup>
                <FieldLabel>映射字段（按文件名匹配对应记录）</FieldLabel>
                <Select
                    options={matchFieldOptions}
                    value={matchFieldId}
                    onChange={onMatchFieldChange}
                    placeholder="请选择映射字段"
                    searchable
                />
                <FieldHint>将根据 md 文件名（去除后缀）与该字段的值进行匹配，未匹配到的文件将被跳过</FieldHint>
            </FieldGroup>
        </SettingContainer>
    )
}
